import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { yearMonthString, shiftMonth } from '../../data/functions';

// How many months to render on each side of "today" for the very first paint.
const INITIAL_BEFORE = 6;
const INITIAL_AFTER = 6;
// How many months to add each time the user scrolls near an edge.
const MONTHS_PER_LOAD = 6;
// Distance (px) from an edge at which we start loading more months.
const LOAD_THRESHOLD_PX = 800;

// Build an inclusive list of "YYYY-MM" strings from startMonth to endMonth.
function buildMonths(startMonth, endMonth) {
  if (!startMonth || !endMonth) return [];
  const [sy, sm] = startMonth.split('-').map(Number);
  const [ey, em] = endMonth.split('-').map(Number);
  const months = [];
  let y = sy;
  let m = sm;
  while (y < ey || (y === ey && m <= em)) {
    months.push(`${y}-${String(m).padStart(2, '0')}`);
    m++;
    if (m > 12) { m = 1; y++; }
  }
  return months;
}

function todayMonthStr() {
  return yearMonthString(new Date());
}

/**
 * useInfiniteCalendar - stable, jitter-free infinite scroll engine for the calendar.
 *
 * Design goals:
 * - The month list is pure date math (no dependency on calendarData), so the calendar is
 *   fully built on the very first render, before any data arrives.
 * - The initial range is ALWAYS symmetric around today (equal months before/after), so the
 *   correct position is simply the vertical centre of the content. We set scrollTop in a
 *   layout effect (synchronously, before the browser paints) using the content midpoint, so
 *   there is no "render at top then jump" jitter and no element measurement is needed.
 * - Owns its month range internally so prop/redux updates never reset the view or loop.
 * - Loads more months on scroll, compensating scroll position when prepending so the viewport
 *   stays visually fixed.
 * - Notifies the parent of range changes so data-fetching ranges / prediction windows extend.
 *
 * @returns {{ scrollRef, months, handleScroll, scrollToCenter }}
 */
export default function useInfiniteCalendar({
  onStartMonthChange,
  onEndMonthChange,
}) {
  // Internal range is ALWAYS symmetric around today so the content centre === today.
  // Props are intentionally ignored here; the parent only receives range updates (below)
  // for data fetching. This guarantees the centre-scroll positioning is always correct.
  const [range, setRange] = useState(() => {
    const today = todayMonthStr();
    return {
      start: shiftMonth(today, -INITIAL_BEFORE),
      end: shiftMonth(today, INITIAL_AFTER),
    };
  });

  const months = useMemo(() => buildMonths(range.start, range.end), [range.start, range.end]);

  const scrollRef = useRef(null);
  // True once we have performed the initial centre positioning.
  const didInitialScroll = useRef(false);
  // Guards against firing multiple loads inside a single scroll burst.
  const isLoadingRef = useRef(false);
  // When set, the next layout pass restores scroll position after a prepend.
  const prependAnchor = useRef(null);

  // Centre the scroll container on its content midpoint. Because the initial range is
  // symmetric around today, the midpoint is today's month.
  const scrollToCenter = useCallback((behavior = 'auto') => {
    const container = scrollRef.current;
    if (!container) return false;
    const max = container.scrollHeight - container.clientHeight;
    if (max <= 0) return false; // content not tall enough / not laid out yet
    const top = max / 2;
    if (behavior === 'smooth') {
      container.scrollTo({ top, behavior: 'smooth' });
    } else {
      container.scrollTop = top;
    }
    return true;
  }, []);

  // Notify the parent once of the initial (symmetric) range so data is fetched for it.
  useEffect(() => {
    if (onStartMonthChange) onStartMonthChange(range.start);
    if (onEndMonthChange) onEndMonthChange(range.end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Runs synchronously before paint after every months change.
  // 1) Restores scroll position after a prepend (keeps viewport fixed).
  // 2) Performs the one-time initial centre positioning.
  useLayoutEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    if (prependAnchor.current != null) {
      const added = container.scrollHeight - prependAnchor.current.prevScrollHeight;
      container.scrollTop = prependAnchor.current.prevScrollTop + added;
      prependAnchor.current = null;
      isLoadingRef.current = false;
      return;
    }

    if (!didInitialScroll.current) {
      didInitialScroll.current = scrollToCenter('auto');
    }

    // Appends settle here once the new content is in the DOM.
    isLoadingRef.current = false;
  }, [months, scrollToCenter]);

  const loadEarlier = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    prependAnchor.current = {
      prevScrollHeight: container.scrollHeight,
      prevScrollTop: container.scrollTop,
    };
    setRange((r) => {
      const newStart = shiftMonth(r.start, -MONTHS_PER_LOAD);
      if (onStartMonthChange) onStartMonthChange(newStart);
      return { ...r, start: newStart };
    });
  }, [onStartMonthChange]);

  const loadLater = useCallback(() => {
    setRange((r) => {
      const newEnd = shiftMonth(r.end, MONTHS_PER_LOAD);
      if (onEndMonthChange) onEndMonthChange(newEnd);
      return { ...r, end: newEnd };
    });
  }, [onEndMonthChange]);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container || isLoadingRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    if (scrollTop <= LOAD_THRESHOLD_PX) {
      isLoadingRef.current = true;
      loadEarlier();
      return;
    }

    if (scrollHeight - scrollTop - clientHeight <= LOAD_THRESHOLD_PX) {
      isLoadingRef.current = true;
      loadLater();
    }
  }, [loadEarlier, loadLater]);

  return { scrollRef, months, handleScroll, scrollToCenter };
}
